import { useEffect, useRef } from 'react'
import type { Identity } from '@ory/client'

import { useLocale } from '../LocaleContext'
import { parseShortLocale } from '../locales.constants'
import type { LocaleChangeSource } from '../utils/locale-telemetry'

// Narrow `Identity.traits` (typed as `object` in @ory/client) down to a
// concrete locale string without an `as` cast — keeps TS honest about
// type-drift after a client bump instead of silently returning garbage.
const readLocaleTrait = (identity: Identity | null | undefined): string | null => {
  const traits = identity?.traits
  if (
    traits !== null &&
    typeof traits === 'object' &&
    'locale' in traits &&
    typeof traits.locale === 'string'
  ) {
    return traits.locale
  }
  return null
}

interface Options {
  /** The signed-in identity — null/undefined while the flow is still loading. */
  identity: Identity | null | undefined
  /** Telemetry source attributed to the resulting change. */
  source: LocaleChangeSource
}

// Makes the signed-in identity's profile language govern the UI. Every screen
// that sits behind a session should call this AND hide the header switcher
// (SplitAuthLayout's `showLanguageSwitcher={false}`), so a signed-in user reads
// the language their account carries rather than whatever this tab resolved.
//
// Re-applied on every mount, deliberately: a `paretos_lang` cookie written
// anywhere else — cockpit's switcher, an anonymous visit to /login, a `?lang=`
// link — must not outvote the profile once we know whose session this is.
export const useIdentityLocale = ({ identity, source }: Options): void => {
  const { locale, setLocale } = useLocale()
  // At most one apply per mount. The effect's `locale` dep would otherwise
  // re-fire it after any other setLocale caller (e.g. the provider's `?lang=`
  // handler), double-counting telemetry and fighting that caller for control.
  const appliedRef = useRef(false)

  useEffect(() => {
    if (appliedRef.current) {
      return
    }
    if (!identity?.id) {
      return
    }
    const next = parseShortLocale(readLocaleTrait(identity))
    if (next && next !== locale) {
      setLocale(next, source)
    }
    appliedRef.current = true
  }, [identity, locale, setLocale, source])
}
